import { useEffect } from 'react';
import { NavLink, Navigate, Outlet } from 'react-router-dom';
import { useOrganizationList } from '@clerk/react';
import { Settings, MapPin, Briefcase, Users } from 'lucide-react';
import { useClerkReady } from '../../lib/useClerkReady';
import AuthUnavailable from '../../components/AuthUnavailable';
import SignInPrompt from '../../components/SignInPrompt';

export default function AdminLayout() {
  const { isLoaded, isSignedIn, orgId, orgRole, timedOut } = useClerkReady();
  // userMemberships:true is required for the list to actually populate;
  // the default fetches nothing. setActive lets us flip the active org on
  // the current session so the next getToken() includes org_id.
  const list = useOrganizationList({ userMemberships: true });

  // Clerk's <CreateOrganization /> creates the org but does NOT always set it
  // as the user's active org on the existing session. The JWT then comes
  // back without org_id and our backend returns 403 no_active_org. Same
  // failure mode when an invited user signs in for the first time. Fix:
  // when the user is signed in, has memberships, but no active orgId,
  // promote the first membership to active. After setActive resolves,
  // Clerk re-issues the token with org claims and the page re-renders.
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (orgId) return;
    if (!list.isLoaded || !list.setActive) return;
    const memberships = list.userMemberships?.data ?? [];
    if (memberships.length === 0) return;
    const first = memberships[0];
    if (!first) return;
    list.setActive({ organization: first.organization.id }).catch((err) => {
      console.warn('[admin-layout] setActive failed', err);
    });
  }, [isLoaded, isSignedIn, orgId, list.isLoaded, list.userMemberships?.data, list.setActive]);

  if (!isLoaded) return timedOut ? <AuthUnavailable /> : <p className="muted">Loading…</p>;

  if (!isSignedIn) {
    return (
      <SignInPrompt
        title="Sign in to admin"
        message="Sign in to manage your organization's settings, locations, services, and staff."
      />
    );
  }

  if (!orgId) {
    // Signed in, no active org. Two sub-cases:
    //   1. They have memberships → setActive is in flight, show a brief
    //      activation message (effect above will re-render with orgId set).
    //   2. They have zero memberships → genuine onboarding case, route
    //      through the owner/staff gate.
    if (!list.isLoaded) return <p className="muted">Loading…</p>;
    const memberships = list.userMemberships?.data ?? [];
    if (memberships.length === 0) return <Navigate to="/welcome" replace />;
    return <p className="muted">Activating your organization…</p>;
  }

  // Non-admin staff have no business on /admin/*. Backend already 403s their
  // writes, but the SPA was happily rendering read-only views to them too —
  // hide the whole section and bounce them to their dashboard.
  if (orgRole !== 'org:admin') {
    return <Navigate to="/staff" replace />;
  }

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    `tab${isActive ? ' active' : ''}`;

  return (
    <>
      <nav className="tabs" aria-label="Admin sections">
        <NavLink to="settings" className={tabClass}>
          <Settings size={14} /> Settings
        </NavLink>
        <NavLink to="locations" className={tabClass}>
          <MapPin size={14} /> Locations
        </NavLink>
        <NavLink to="services" className={tabClass}>
          <Briefcase size={14} /> Services
        </NavLink>
        <NavLink to="staff" className={tabClass}>
          <Users size={14} /> Staff
        </NavLink>
        {/* API keys + Custom domains tabs hidden for now — no current use case.
            Routes stay mounted in App.tsx so existing bookmarks don't 404. */}
      </nav>
      <Outlet />
    </>
  );
}
